import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { endTrial } from '../services/billingClient';
import { devLog, devError } from '../utils/logger';

const TrialCountdown = ({ user, userData }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const trialEndRaw = userData?.trial_end_date ?? userData?.trialEndDate;
    if (!trialEndRaw) return;

    const checkTrialStatus = () => {
      const now = new Date();
      const trialEnd = trialEndRaw.toDate ? trialEndRaw.toDate() : new Date(trialEndRaw);
      const timeRemaining = trialEnd.getTime() - now.getTime();

      devLog('Trial Countdown Debug:', {
        now: now.toISOString(),
        trialEnd: trialEnd.toISOString(),
        timeRemaining: timeRemaining,
        daysRemaining: Math.floor(timeRemaining / (1000 * 60 * 60 * 24)),
      });

      if (timeRemaining <= 0) {
        // Trial has expired - convert to Starter plan
        handleTrialExpiration();
        setIsExpired(true);
        setTimeLeft(0);
      } else {
        setTimeLeft(timeRemaining);
      }
    };

    checkTrialStatus();
    const interval = setInterval(checkTrialStatus, 1000); // Check every second

    return () => clearInterval(interval);
  }, [userData]);

  const handleTrialExpiration = async () => {
    try {
      await endTrial();
    } catch (error) {
      devError('Error ending trial:', error);
    }
  };

  const formatTime = (milliseconds) => {
    if (milliseconds <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };

    const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
    const hours = Math.floor((milliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  };

  const { days, hours, minutes, seconds } = formatTime(timeLeft);
  
  // Only show detailed countdown in the last 3 hours (3 * 60 * 60 * 1000 = 10,800,000 ms)
  const showDetailedCountdown = timeLeft && timeLeft <= 10800000; // 3 hours in milliseconds

  if (!(userData?.is_trial ?? userData?.isTrial)) return null;

  if (isExpired) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-red-800">Trial Expired</h3>
            <p className="text-red-600">
              Your trial has ended. You are now on the Free plan. Upgrade to Pro anytime from Settings.
            </p>
          </div>
          <button
            onClick={() => navigate('/dashboard/settings?plans=1')}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
          >
            Plan & billing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-blue-800">Free Trial Active</h3>
          <p className="text-blue-600">Time remaining in your trial</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-blue-800">
            {showDetailedCountdown 
              ? `${days > 0 ? `${days}d ` : ''}${hours}h ${minutes}m ${seconds}s`
              : `${days} day${days !== 1 ? 's' : ''} remaining`
            }
          </div>
          <p className="text-sm text-blue-600">
            Trial ends: {userData?.trialEndDate ? 
              new Date(userData.trialEndDate.toDate ? userData.trialEndDate.toDate() : userData.trialEndDate).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
              }) : 'Unknown'
            }
          </p>
          <p className="text-sm text-blue-600">Auto-converts to Starter Plan</p>
        </div>
      </div>
    </div>
  );
};

export default TrialCountdown; 